// filepath: apps/web/src/features/proc/ProcRoute.tsx
//
// FR-UI-PROC-1 — route shell for the failed-instance dashboard and
// per-instance execution-trace viewer.
//
// The route shape is determined by the URL:
//   - /processes/instances/failed           → FailedInstanceDashboardPage
//   - /processes/instances/:instanceId/trace → ExecutionTracePage
//
// Mirrors the TasksRoute pattern (apps/web/src/features/tasks/TasksRoute.tsx):
//   - reads the URL via `window.location.pathname` (no react-router
//     dependency — App.tsx convention);
//   - delegates to <FailedInstanceDashboardPage /> or
//     <ExecutionTracePage />;
//   - surfaces the trace-viewer page back-link via a minimal
//     pushState navigation.

import React from "react";
import { FailedInstanceDashboardPage } from "./FailedInstanceDashboardPage";
import { ExecutionTracePage } from "./ExecutionTracePage";

const DASHBOARD_PATH_RE = /^\/processes\/instances\/failed\/?$/;
const TRACE_PATH_RE = /^\/processes\/instances\/([^/]+)\/trace\/?$/;

export interface ProcRouteProps {
  /**
   * Optional override for the trace-viewer's "View trace"
   * click-through. When omitted, the route uses a default
   * pushState navigation to the trace page.
   */
  onViewTrace?: (instanceId: string) => void;
  /**
   * Optional override for the trace-viewer's back-to-dashboard
   * click-through. When omitted, the route uses a default
   * pushState navigation to the dashboard.
   */
  onBackToDashboard?: () => void;
}

function defaultPushTrace(instanceId: string): void {
  if (typeof window === "undefined") return;
  window.history.pushState(
    {},
    "",
    `/processes/instances/${encodeURIComponent(instanceId)}/trace`,
  );
  window.dispatchEvent(new PopStateEvent("popstate"));
}

function defaultBackToDashboard(): void {
  if (typeof window === "undefined") return;
  window.history.pushState({}, "", "/processes/instances/failed");
  window.dispatchEvent(new PopStateEvent("popstate"));
}

function extractInstanceId(pathname: string): string | null {
  const m = pathname.match(TRACE_PATH_RE);
  if (!m || !m[1]) return null;
  try {
    return decodeURIComponent(m[1]);
  } catch {
    return null;
  }
}

export function ProcRoute({
  onViewTrace,
  onBackToDashboard,
}: ProcRouteProps): React.ReactElement | null {
  const pathname =
    typeof window !== "undefined" ? window.location.pathname : "/";

  if (DASHBOARD_PATH_RE.test(pathname)) {
    return (
      <FailedInstanceDashboardPage
        onViewTrace={onViewTrace ?? defaultPushTrace}
      />
    );
  }

  const instanceId = extractInstanceId(pathname);
  if (instanceId !== null) {
    return (
      <ExecutionTracePage
        instanceId={instanceId}
        onBackToDashboard={onBackToDashboard ?? defaultBackToDashboard}
      />
    );
  }

  return (
    <main data-testid="proc-route-no-match">
      <p>Unknown proc route.</p>
    </main>
  );
}
